import React, { useEffect, useMemo, useState } from 'react';
import {Navbar, Nav, Container, NavDropdown} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleUser } from '@fortawesome/free-regular-svg-icons';
import config from '../config';
import { useAuthContext } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import api from '../lib/request';
import { buildLoginPathWithGoback } from '../lib/authRedirect';

/*
<a
              className={`nav-link px-2 ${pathIs(platformPath) ? 'active-nav' : ''}`}
              aria-current={pathIs(platformPath) ? 'page' : undefined}
              href={platformPath}
              data-text="Platform"
            >
              Platform
            </a>
            */

function HeaderNavbar({extraContent, extraUserContent, allowAdminContent}) {
  const { isAuthenticated, isSiteAdmin, logout, userInfo } = useAuthContext();
  const showPrivilegedContent = isAuthenticated && (typeof allowAdminContent === 'boolean' ? allowAdminContent : isSiteAdmin);
  const location = useLocation();
  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);

  const normalize = (p) => (p || '').replace(/\/$/, '');
  const pathIs = (path) => normalize(location.pathname) === normalize(path);
  const homePath = `${config.prefixPath}`;
  const projectsPath = `${config.prefixPath}/projects`;
  const contactPath = `${config.prefixPath}/contact`;
  const settingsPath = `${config.prefixPath}/settings`;
  const getStartedPath = `${config.prefixPath}#get-started`;
  const loginPath = useMemo(
    () => buildLoginPathWithGoback({
      pathname: location.pathname,
      search: location.search,
      hash: location.hash
    }, config.prefixPath),
    [location.pathname, location.search, location.hash]
  );

  function goToLogout () {
    logout();
  }

  useEffect(() => {
    let active = true;
    if (!isAuthenticated) {
      setOrganizations([]);
      setOrgsLoading(false);
      return () => {
        active = false;
      };
    }
    setOrgsLoading(true);
    api.get('/api/v1/organizations/mine')
      .then((data) => {
        if (!active) return;
        const orgs = Array.isArray(data?.organizations) ? data.organizations : [];
        setOrganizations(orgs);
      })
      .catch(() => {
        if (!active) return;
        setOrganizations([]);
      })
      .finally(() => {
        if (!active) return;
        setOrgsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isAuthenticated]);

  const displayUsername = useMemo(() => {
    if (!userInfo) return '';
    return userInfo.username || '';
  }, [userInfo]);

  const displayFullName = useMemo(() => {
    if (!userInfo) return '';
    const fromParts = [userInfo.firstName, userInfo.lastName].filter(Boolean).join(' ').trim();
    return userInfo.name || fromParts || '';
  }, [userInfo]);

  const organizationNames = useMemo(() => {
    if (!Array.isArray(organizations)) return [];
    return organizations
      .map((org) => (org?.name || org?.slug || org?.id))
      .filter(Boolean);
  }, [organizations]);

  const showOrganizationsSection = isSiteAdmin || orgsLoading || organizationNames.length > 0;

  return (
    <Navbar expand="md"  variant="light" className="sticky-top top-nav">
      <Container>
        <Navbar.Brand href={homePath}>
          <img style={{marginRight: 10}} src="/logo.svg" alt="Logo" width="30" height="26" className="d-inline-block align-text-top"/>
          <span>{config.siteName}</span>
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="main-navbar" className="ms-auto" />
        <Navbar.Collapse id="main-navbar">
          <Nav className="me-auto primary-nav-links">
            <a
              className={`nav-link px-2 ${pathIs(projectsPath) ? 'active-nav' : ''}`}
              aria-current={pathIs(projectsPath) ? 'page' : undefined}
              href={projectsPath}
              data-text="Projects"
            >
              Projects
            </a>
            
            <a
              className={`nav-link px-2 ${pathIs(contactPath) ? 'active-nav' : ''}`}
              aria-current={pathIs(contactPath) ? 'page' : undefined}
              href={contactPath}
              data-text="Contact"
            >
              Contact
            </a>
            <a
              className={`btn btn-sm btn-primary ms-2 ${pathIs(getStartedPath) ? 'active-nav' : ''}`}
              aria-current={pathIs(getStartedPath) ? 'page' : undefined}
              href={getStartedPath}
              data-text="Get Started"
            >
              Get Started
            </a>
            {(extraContent && showPrivilegedContent) ? extraContent : null}
          </Nav>
          <Nav className="ms-auto">
            {isAuthenticated ? (
              <>
                {showPrivilegedContent && extraUserContent ? extraUserContent : null}
                <NavDropdown
                  className="account-dropdown"
                  align="end"
                  title={<FontAwesomeIcon title="Account" icon={faCircleUser} size="lg" />}
                  id="basic-nav-dropdown"
                >
                  <NavDropdown.Header>Account</NavDropdown.Header>
                  <NavDropdown.Item as="div" className="dropdown-item-text">
                    <div className="small text-muted">Username</div>
                    <div className="small">{displayUsername || 'Unknown'}</div>
                  </NavDropdown.Item>
                  <NavDropdown.Item as="div" className="dropdown-item-text">
                    <div className="small text-muted">Name</div>
                    <div className="small">{displayFullName || 'Not set'}</div>
                  </NavDropdown.Item>
                  {showOrganizationsSection ? (
                    <NavDropdown.Item as="div" className="dropdown-item-text">
                      <div className="small text-muted">Organizations</div>
                      {orgsLoading ? (
                        <div className="small">Loading...</div>
                      ) : organizationNames.length ? (
                        <div className="small">{organizationNames.join(', ')}</div>
                      ) : null}
                      {isSiteAdmin ? <div className="small text-muted">Admin</div> : null}
                    </NavDropdown.Item>
                  ) : null}
                  <NavDropdown.Divider />
                  <NavDropdown.Item href={settingsPath}>Settings</NavDropdown.Item>
                  <NavDropdown.Item style={{zIndex: 3000}} onClick={goToLogout}>Log out</NavDropdown.Item>
                </NavDropdown>
              </>
            ) : (
              <a className="nav-link" href={loginPath}>Log in</a>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

export default HeaderNavbar;
